import { Injectable, computed, signal } from '@angular/core';

/** Shape of a recently-visited RIPT summary entry. */
export interface RecentRecord {
  id: string;
  title: string;
  visitedAt: number;
}

const STORAGE_KEY = 'ript.recentRecords';
const MAX_RECENT = 5;

/**
 * Salesforce-style "Recent Records" cache for the RIPT Summaries dropdown
 * (nav tab). Backed by `localStorage` and exposed as a signal so any consumer
 * (menu, breadcrumb, etc.) reacts to new visits. Entries are de-duplicated by
 * `id` and capped at {@link MAX_RECENT}.
 */
@Injectable({ providedIn: 'root' })
export class RecentRecordsService {
  private readonly _records = signal<RecentRecord[]>(this.readStorage());
  /** Latest entries first. */
  readonly records = computed(() => this._records());

  /** Record a visit. Newest wins, dedupe by id, cap to {@link MAX_RECENT}. */
  push(entry: { id: string; title: string | null | undefined }): void {
    if (!entry.id) return;
    const title = (entry.title ?? entry.id).toString();
    const next: RecentRecord = { id: entry.id, title, visitedAt: Date.now() };
    const merged = [next, ...this._records().filter((r) => r.id !== entry.id)].slice(0, MAX_RECENT);
    this._records.set(merged);
    this.writeStorage(merged);
  }

  /** Remove a single entry by id (e.g. after the record is deleted or no longer exists). */
  remove(id: string | null | undefined): void {
    if (!id) return;
    const key = id.toString();
    const current = this._records();
    const next = current.filter((r) => r.id !== key);
    if (next.length === current.length) return;
    this._records.set(next);
    this.writeStorage(next);
  }

  /** Manually clear the cache (used by tests / user "clear history"). */
  clear(): void {
    this._records.set([]);
    this.writeStorage([]);
  }

  private readStorage(): RecentRecord[] {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(
          (item): item is RecentRecord =>
            typeof item === 'object' &&
            item !== null &&
            typeof (item as RecentRecord).id === 'string' &&
            typeof (item as RecentRecord).title === 'string',
        )
        .slice(0, MAX_RECENT);
    } catch {
      return [];
    }
  }

  private writeStorage(records: RecentRecord[]): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
      // Quota exceeded or private mode — silently ignore; menu still works via signal.
    }
  }
}
